import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { TranslationConfigService } from '../../../services/translation-config.service';
import { DeletionRequestDialogData } from '../modules/shared/deletion-request-dialog/deletion-request-dialog.component';
import { ConfigService } from '../../../services/config.service';

export enum NotificationType {
    Public = 'public',
    Shipment = 'shipment',
    Service = 'service',
}

@Injectable()
export class UserManagementService {
    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private translationConfigService: TranslationConfigService
    ) {}

    getContacts(companyId: string): Observable<Contact[]> {
        return this.http
            .get(`${environment.http.userManagementBaseUrl}companies/${companyId}/contacts`, {
                headers: this.getLanguageHeader(),
            })
            .pipe(
                map((res) => {
                    return res['contacts'];
                })
            );
    }

    getContact(contactId: string): Observable<Contact> {
        return this.http.get<Contact>(`${environment.http.userManagementBaseUrl}contacts/${contactId}`);
    }

    deleteContact(contactId: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(`${environment.http.userManagementBaseUrl}contacts/${contactId}`);
    }

    getFunctionalNotifications(): Observable<Notifications> {
        return this.http.get<Notifications>(`${environment.http.userManagementBaseUrl}notifications`).pipe(
            map((notifications) => {
                notifications.public.type = NotificationType.Public;
                notifications.shipment.type = NotificationType.Shipment;
                notifications.service.type = NotificationType.Service;

                return notifications;
            })
        );
    }

    updateNotification(type: NotificationType, notification: UpdatableNotification): Observable<Notification> {
        return this.http.patch<Notification>(
            `${environment.http.userManagementBaseUrl}notifications/${type}`,
            notification
        );
    }

    getRoles(): Observable<Role[]> {
        return this.http
            .get<Role[]>(`${environment.http.userManagementBaseUrl}roles`, { headers: this.getLanguageHeader() })
            .pipe(
                map((roles) => {
                    const customRoles = roles.filter((r) => r.type === 'Custom');
                    const defaultRolesSorted = [
                        'CEO',
                        'Administrator',
                        'Employee',
                        'Technician',
                        'Backoffice',
                        'Trainee',
                    ]
                        .map((name) => roles.find((r) => r.type === 'Default' && r.id.split('_')?.[2] === name))
                        .filter((v) => v !== undefined);

                    return [...defaultRolesSorted, ...customRoles];
                })
            );
    }

    getRole(roleId: string): Observable<Role> {
        return this.http.get<Role>(`${environment.http.userManagementBaseUrl}roles/${roleId}`, {
            headers: this.getLanguageHeader(),
        });
    }

    createRole(creatableRole: CreatableRole): Observable<void> {
        return this.http.post<void>(`${environment.http.userManagementBaseUrl}roles`, creatableRole);
    }

    updateRole(roleId: string, updateableRole: UpdateableRole): Observable<void> {
        return this.http.patch<void>(
            `${environment.http.userManagementBaseUrl}roles/${encodeURIComponent(roleId)}`,
            updateableRole
        );
    }

    deleteRole(roleId: string): Observable<void> {
        return this.http.delete<void>(`${environment.http.userManagementBaseUrl}roles/${encodeURIComponent(roleId)}`);
    }

    assignRoleToUser(roleId: string, loginId: string): Observable<void> {
        return this.http.patch<void>(`${environment.http.userManagementBaseUrl}user/${loginId}/roles`, { roleId });
    }

    getLegacyUserIds(companyId: string): Observable<LegacyUserId[]> {
        return this.http.get<LegacyUserId[]>(
            `${environment.http.userManagementBaseUrl}companies/${companyId}/users/ids`
        );
    }

    updateLegacyUserId(userId: string, legacyUserId: string): Observable<void> {
        return this.http.post<void>(`${environment.http.userManagementBaseUrl}users/${userId}/migrate-legacy-user`, {
            legacyUserId,
        });
    }

    deleteLegacyUserId(userId: string, legacyUserId: string): Observable<void> {
        return this.http.request<void>(
            'delete',
            `${environment.http.userManagementBaseUrl}users/${userId}/migrate-legacy-user`,
            {
                body: {
                    legacyUserId,
                },
            }
        );
    }

    createOrderLimit(loginId: string, limit: number): Observable<void> {
        return this.http.post<void>(`${environment.http.userManagementBaseUrl}users/${loginId}/order-limit`, { limit });
    }

    getOrderLimit(loginId: string): Observable<{ limit: number }> {
        return this.http.get<{ limit: number }>(
            `${environment.http.userManagementBaseUrl}users/${loginId}/order-limit`
        );
    }

    updateOrderLimit(loginId: string, limit: number): Observable<void> {
        return this.http.patch<void>(`${environment.http.userManagementBaseUrl}users/${loginId}/order-limit`, {
            limit,
        });
    }

    deleteOrderLimit(loginId: string): Observable<void> {
        return this.http.delete<void>(`${environment.http.userManagementBaseUrl}users/${loginId}/order-limit`);
    }

    createDeletionRequest(deletionRequestPayload: DeletionRequestDialogData): Observable<{ id: string }> {
        return this.http.post<{ id: string }>(`${environment.http.userManagementBaseUrl}cases`, {
            firstName: deletionRequestPayload.contact.firstName,
            lastName: deletionRequestPayload.contact.lastName,
            email: deletionRequestPayload.contact.email,
            phoneNumber:
                [
                    deletionRequestPayload.contact?.phone?.code,
                    deletionRequestPayload.contact?.phone?.number,
                    deletionRequestPayload.contact?.phone?.extension,
                ].join('') || undefined,
            customerNumber: deletionRequestPayload.customerNumber,
            countryCode: deletionRequestPayload.countryCode,
        });
    }

    private getLanguageHeader(): HttpHeaders {
        const languageCode = this.translationConfigService.getLanguage()
            ? this.translationConfigService.getLanguage()
            : 'en-US';
        return new HttpHeaders().append('accept-language', languageCode);
    }
}

export interface Contact {
    id: string;
    salutation: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: {
        country: string;
        code: string;
        number: string;
        extension?: string;
    };
    mobilePhone?: Phone;
    uuid?: string;
    userState?: string;
    emailConfirmed?: boolean;
    ceo?: boolean;
    loginId?: string;
    prioRouting?: boolean;
    businessRole: Role;
    hasActiveCases?: boolean;
}

export interface Notifications {
    public: Notification;
    shipment: Notification;
    service: Notification;
}

export interface Notification {
    type: NotificationType;
    enabled: boolean;
    valid: boolean;
    email: string;
    phone?: Phone;
    mobilePhone?: Phone;
}

export interface Phone {
    country?: string;
    code?: string;
    number: string;
    extension?: string;
}

export interface UpdatableNotification {
    enabled?: boolean;
    email?: string;
    phone?: Phone;
    mobilePhone?: Phone;
}

export interface CreatableRole {
    roleId: string;
    displayName: string;
    description: string;
    applicationRoles?: string[]; // Application roles
}

export interface UpdateableRole {
    displayName?: string;
    description?: string;
    applicationRoles?: string[];
}

export interface Role {
    id: string;
    type: 'Default' | 'Custom';
    name: string;
    description: string;
    apps: Application[];
    users?: string[];
    position?: number; // Need for sorting
}

export interface Application {
    id: string;
    name: string;
    activated: boolean;
    description: string;
    applicationRoles: ApplicationRole[];
}

export interface ApplicationRole {
    id: string;
    name: string;
    description: string;
    activated?: boolean;
}

export interface LegacyUserId {
    legacyUserId: string;
    iamAccountId: string;
}
